// Caller-holdings clamps for the role editor (LEAF module: relative imports only, so it is
// unit-testable like roleCatalogue.js / wildcardExpansion.js).
//
// "Grant only what you hold": a scoped (non superadmin) editor may only seed or raise a cell up
// to their OWN rank on that cell. The three clamps below were extracted from editRole.js on
// 2026-09-08 and made WILDCARD-AWARE: a caller whose role holds a "*" (grant-all) row on an axis
// holds every solution / every module on that axis at the "*" tab's level, so the effective
// caller rank for a cell is max(wildcard rank, enumerated rank). They used to read the
// enumerated rows only, which clamped a "*"-holding delegated admin to nothing (the per-cell
// helpers callerRankFor / callerModuleRankFor in the editor already took the max).
import { rank as permRank, isScopingAsset } from "./permissionDependencies";

const permOf = (r) => (r >= 2 ? "ALL" : r >= 1 ? "READ" : "disable");

// The rank of a "*" row's "*" tab on one axis (0 when the caller holds no "*" row there).
// `rows` are role-doc rows ({name|serviceId, tabs:[{name, permission}]}).
export function wildRankOf(rows, key) {
  const w = (rows || []).find((r) => r && String(r[key]) === "*");
  return w ? Math.max(0, ...(w.tabs || []).map((t) => (t && t.name === "*" ? permRank(t.permission) : 0))) : 0;
}

// FE-4: build the create-time utilities-matrix seed for a delegated (non-superadmin) admin by
// clamping the full module catalog to what the caller holds: each tab defaults to the caller's
// own level on it (the enumerated tab, or the "*" module row's level, whichever is higher), or
// "disable" if they hold neither, so a NEW role defaults to a grantable modules set.
export function clampUtilsToCaller(base, callerModules) {
  const held = {};
  (callerModules || []).forEach((m) => {
    if (m && m.name) {
      held[m.name] = {};
      (m.tabs || []).forEach((t) => {
        if (t && t.name) held[m.name][t.name] = permRank(t.permission);
      });
    }
  });
  const wild = wildRankOf(callerModules, "name");
  return (base || []).map((mod) => {
    const h = held[mod.name] || {};
    const tabs = (mod.tabs || []).map((t) =>
      t && t.name ? { ...t, permission: permOf(Math.max(wild, h[t.name] || 0)) } : t
    );
    const named = tabs.filter((t) => t && t.name);
    const overall =
      named.length && named.every((t) => t.permission === "ALL")
        ? "ALL"
        : named.length && named.every((t) => t.permission === "READ")
          ? "READ"
          : named.length && named.every((t) => t.permission === "disable")
            ? "disable"
            : "";
    return { ...mod, tabs, overall };
  });
}

// FE-3/item-5b: clamp solutions-matrix cells to the caller's OWN per-cell rank
// (heldServiceTabs = { [serviceId]: { [assetName]: rank } } from the caller role doc), lifted by
// the caller's "*" solution rank (wildServiceRank). Overall stays an OPERATIONAL summary.
export function clampServicesToCaller(perms, heldServiceTabs, wildServiceRank = 0) {
  return (perms || []).map((row) => {
    const held = (heldServiceTabs && heldServiceTabs[row.serviceId]) || {};
    const assets = (row.assets || []).map((a) => {
      if (!a || !a.name) return a;
      const r = Math.min(permRank(a.permissions), Math.max(wildServiceRank, held[a.name] || 0));
      return { ...a, permissions: permOf(r) };
    });
    // D26 (2026-09-25): the scoping columns are left out, as everywhere else in the editor, so
    // a delegated admin's row Overall "Full" shows as chosen (they made the row read mixed).
    const named = assets.filter((a) => a && a.name && !isScopingAsset(a.name));
    const overall =
      named.length && named.every((a) => a.permissions === "ALL")
        ? "ALL"
        : named.length && named.every((a) => a.permissions === "READ")
          ? "READ"
          : named.length && named.every((a) => !a.permissions || a.permissions === "disable")
            ? "disable"
            : "";
    return { ...row, assets, overall };
  });
}

// Item 5b/7: clamp BOTH matrices to the caller's own holdings after any matrix mutation by a
// scoped editor (a bulk action could otherwise raise cells the caller never sees).
// min(cell, callerRank) leaves every legitimate grant untouched.
export function clampPermsToCallerHoldings(
  utilsAtom,
  servicesAtom,
  heldModuleTabs,
  heldServiceTabs,
  wildModuleRank = 0,
  wildServiceRank = 0
) {
  const utils = (utilsAtom || []).map((mod) => {
    const held = (heldModuleTabs && heldModuleTabs[mod.name]) || {};
    const tabs = (mod.tabs || []).map((t) => {
      if (!t || !t.name) return t;
      const r = Math.min(permRank(t.permission), Math.max(wildModuleRank, held[t.name] || 0));
      return { ...t, permission: permOf(r) };
    });
    const named = tabs.filter((t) => t && t.name);
    const overall =
      named.length && named.every((t) => t.permission === "ALL")
        ? "ALL"
        : named.length && named.every((t) => t.permission === "READ")
          ? "READ"
          : named.every((t) => !t.permission || t.permission === "disable")
            ? "disable"
            : "";
    return { ...mod, tabs, overall };
  });
  return {
    utils,
    servicePermissions: clampServicesToCaller(servicesAtom, heldServiceTabs, wildServiceRank),
  };
}

// D26 (a) (roles prod promotion, Stage 3, 2026-09-25): what a scoped editor cannot use because
// of their OWN limits is hidden in the role editor, not greyed. A bulk "Full" - a module row's
// Overall, a solution row's Overall, a column master or the "Overall Feature Permissions"
// master - is offered only when every cell it sets can take Full for this editor: otherwise
// clampPermsToCallerHoldings (above) would silently lower the cells the editor holds only at
// Partial, and the choice would not be applied as shown. A cell the editor holds at Partial
// (rank 1) blocks; a cell they do not hold at all (rank 0) is shown as "-" and stays unchanged
// (an editable role holds nothing its editor lacks). The callerRankFor / callerModuleRankFor
// arguments are the editor's own wildcard-aware rank functions.

// Base Permissions: would the module row's Overall "Full" reach a tab held only at Partial?
export function moduleFullBlocked(util, callerModuleRankFor) {
  return ((util && util.tabs) || []).some(
    (t) => t && t.name && callerModuleRankFor(util.name, t.name) === 1
  );
}

// Per-solution matrix: `rows` are the solution rows the control sets; `asset` is the column's
// asset, or null for an Overall (every operational asset - a bulk Full never touches the
// scoping columns). A bulk raise on a scoping column skips rows without a feature
// (servicePermissionChange in editRole.js), so those cells do not count either.
export function bulkFullBlocked(rows, asset, callerRankFor) {
  return (rows || []).some((row) => {
    const cells = ((row && row.assets) || []).filter((a) => a && a.name);
    const hasFeature = cells.some(
      (a) => !isScopingAsset(a.name) && permRank(a.permissions) >= 1
    );
    const touched =
      asset == null
        ? cells.filter((a) => !isScopingAsset(a.name))
        : cells.filter(
            (a) => a.name === asset && (!isScopingAsset(asset) || hasFeature)
          );
    return touched.some((a) => callerRankFor(row.serviceId, a.name) === 1);
  });
}
